import React, { useEffect, useState } from 'react';
import { Table, Select, Input, Button, Space, message, Spin, Switch } from 'antd';
import axiosInstance from '../../../axiosInstance';

const { Option } = Select;

const RolePermissionTable = () => {
    const [roles, setRoles] = useState([]); // Roles displayed in the table
    const [allRoles, setAllRoles] = useState([]); // List of all available roles for dropdown
    const [loading, setLoading] = useState(true); // Loader state

    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const response = await axiosInstance.get('/role-permission');
                setRoles(response.data || []);
            } catch (error) {
                message.error('Error fetching roles and permissions.');
                console.error(error);
            } finally {
                setLoading(false); // Set loading to false after data is fetched
            }
        };

        const fetchAllRoles = async () => {
            try {
                const response = await axiosInstance.get('/all-roles');
                const filteredRoles = response.data.roles.filter(
                    (role) => !roles.some((r) => r.role_id === role.id)
                );
                setAllRoles(filteredRoles);
            } catch (error) {
                message.error('Error fetching all roles.');
                console.error(error);
            }
        };

        fetchRoles();
        fetchAllRoles();
    }, []);

    const saveRoleChange = async (updatedRole) => {
        const permissions = {
            is_stock: updatedRole.is_stock,
            is_sales: updatedRole.is_sales,
            is_manage: updatedRole.is_manage,
        };

        const roleWithPermissions = {
            role_id: updatedRole.role_id,
            permissions: permissions, // Include permissions object
        };

        try {
            let response;
            if (updatedRole.id) {
                response = await axiosInstance.put(`/role-permission/${updatedRole.id}`, roleWithPermissions);
            } else {
                response = await axiosInstance.post('/role-permission', roleWithPermissions);
            }
            const updatedData = response.data;
            setRoles((prevRoles) =>
                prevRoles.map((role) =>
                    role.id === updatedRole.id || role.role_id === updatedRole.role_id
                        ? { ...role, id: updatedData.id } // Update ID if it's a new role
                        : role
                )
            );
            message.success(updatedRole.id ? 'Role updated successfully.' : 'Role created successfully.');
        } catch (error) {
            message.error('Error saving role.');
            console.error(error);
        }
    };

    const handleRoleChange = (index, field, value) => {
        const updatedRole = {
            ...roles[index],
            [field]: value,
        };

        setRoles((prevRoles) => {
            const updatedRoles = [...prevRoles];
            updatedRoles[index] = updatedRole;
            return updatedRoles;
        });

        saveRoleChange(updatedRole); // Save changes automatically
    };

    const addRoleRow = () => {
        setRoles((prevRoles) => [
            ...prevRoles,
            { id: null, role_id: '', is_sales: false, is_manage: false, is_stock: false },
        ]);
    };

    const removeRoleRow = async (index) => {
        const roleToRemove = roles[index];

        if (roleToRemove.id) {
            try {
                await axiosInstance.delete(`/role-permission/${roleToRemove.id}`);
                message.success('Role removed successfully.');
            } catch (error) {
                message.error('Error removing role.');
                console.error(error);
            }
        }

        setRoles((prevRoles) => prevRoles.filter((_, i) => i !== index));
    };

    const columns = [
        {
            title: 'Role',
            dataIndex: 'role_id',
            render: (roleId, record, index) => {
                const selectedRole = allRoles.find((role) => role.id === roleId);

                const isEditable = ![1, 2, 3].includes(roleId); // Disable if role_id is 1, 2, or 3

                return (
                    <Select
                        showSearch
                        value={selectedRole ? selectedRole.name : roleId} // Display role name
                        onChange={(value) => {
                            handleRoleChange(index, 'role_id', value)
                            window.location.reload();
                        }}
                        style={{ width: '100%' }}
                        placeholder="Select Role"
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            option.children.toLowerCase().includes(input.toLowerCase())
                        }
                        disabled={!isEditable} // Disable if role_id is restricted
                    >
                        {allRoles
                            .filter((role) => !roles.some((r) => r.role_id === role.id))
                            .map((role) => (
                                <Option key={role.id} value={role.id}>
                                    {role.name}
                                </Option>
                            ))}
                    </Select>
                );
            },
        },
        {
            title: 'Is Sales',
            dataIndex: 'is_sales',
            render: (_, record, index) => {
                const isEditable = ![1, 2, 3].includes(record.role_id); // Disable if role_id is restricted

                return (
                    <Switch
                        checked={record.is_sales}
                        onChange={(checked) => handleRoleChange(index, 'is_sales', checked)}
                        disabled={!isEditable} // Disable if role_id is restricted
                    />
                );
            },
        },
        {
            title: 'Is Manage',
            dataIndex: 'is_manage',
            render: (_, record, index) => {
                const isEditable = ![1, 2, 3].includes(record.role_id); // Disable if role_id is restricted

                return (
                    <Switch
                        checked={record.is_manage}
                        onChange={(checked) => handleRoleChange(index, 'is_manage', checked)}
                        disabled={!isEditable} // Disable if role_id is restricted
                    />
                );
            },
        },
        {
            title: 'Is Stock',
            dataIndex: 'is_stock',
            render: (_, record, index) => {
                const isEditable = ![1, 2, 3].includes(record.role_id); // Disable if role_id is restricted

                return (
                    <Switch
                        checked={record.is_stock}
                        onChange={(checked) => handleRoleChange(index, 'is_stock', checked)}
                        disabled={!isEditable} // Disable if role_id is restricted
                    />
                );
            },
        },
        {
            title: 'Action',
            render: (_, __, index) => {
                const isEditable = ![1, 2, 3].includes(roles[index].role_id); // Disable if role_id is restricted

                return (
                    <Button
                        type="link"
                        danger
                        onClick={() => removeRoleRow(index)}
                        disabled={!isEditable} // Disable remove button if role_id is restricted
                    >
                        Remove
                    </Button>
                );
            },
        },
    ];

    return (
        <div>
            <h2>Role Permission Table</h2>

            <Table
                dataSource={roles}
                columns={columns}
                pagination={false}
                rowKey={(record) => record.id || `new-${record.role_id}`}
                style={{ marginBottom: '20px' }}
                scroll={{ x: true }}
                loading={loading}
            />

            <Space style={{ marginBottom: '20px', display: 'block', textAlign: 'center' }}>
                <Button type="dashed" onClick={addRoleRow}>
                    Add Role
                </Button>
            </Space>
        </div>
    );
};

export default RolePermissionTable;
